import { message, Table, TablePaginationConfig, Tooltip } from 'antd';
import { ColumnsType, SorterResult } from 'antd/lib/table/interface';
import { toString } from 'lodash';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { QualityContainer } from '~/components/atoms/QualityContainer';
import ErrorMessage from '~/components/molecules/Shared/ErrorMessage';
import { QualityColorEnum } from '~/constants/QualityColor.enum';
import { ISummarySaleStatement } from '~/domain/api';
import { currencyFormatter } from '~/helpers/util/number';
import { SorterEnum } from '~/zustand/productRegistrationRequest/enums';
import { defaultColumnOrderBy } from '~/zustand/salesStatement/useSalesStatementStore';

import { TableContainer } from '~/components/bosons/TableContainer.styled';
import useSaleStatementByTypeStore from '~/zustand/salesStatement/useSalesStatementByTypeStore';
import * as S from './SalesStatementIntermediationSummaryTable.styled';

const rowKey = (row: ISummarySaleStatement) => row.uniqueId;

export const tableColumns: ColumnsType<ISummarySaleStatement> = [
  {
    title: 'Produto',
    dataIndex: 'productLongDescription',
    width: '180px',
    align: 'left',
    sorter: true,
    render: (_: string, saleStatement: ISummarySaleStatement) => (
      <>
        <Tooltip title={saleStatement.productLongDescription}>
          <S.Text>{`${saleStatement.productLongDescription}`}</S.Text>
        </Tooltip>
        <S.GrayText>{`${saleStatement.productCode}`}</S.GrayText>
      </>
    ),
  },
  {
    title: 'QLD.',
    dataIndex: 'qualityDescription',
    width: '60px',
    align: 'center',
    render: (qualityDescription: string) => {
      return (
        <QualityContainer quality={qualityDescription as QualityColorEnum}>
          {qualityDescription}
        </QualityContainer>
      );
    },
  },
  {
    title: 'Preços do Produtor',
    children: [
      {
        title: 'Quantidade Total',
        dataIndex: 'producerTotalQuantity',
        width: '80px',
        align: 'center',
        render: (quantity: number) => <S.Text>{quantity || '-'}</S.Text>,
      },
      {
        title: 'Menor',
        dataIndex: 'producerLowestPrice',
        width: '80px',
        align: 'right',
        render: (price: number) => (price ? currencyFormatter(price, true) : '-'),
      },
      {
        title: 'Média',
        dataIndex: 'producerAveragePrice',
        width: '80px',
        align: 'right',
        render: (price: number) => (price ? currencyFormatter(price, true) : '-'),
      },
      {
        title: 'Maior',
        dataIndex: 'producerHighestPrice',
        width: '80px',
        align: 'right',
        render: (price: number) => (price ? currencyFormatter(price, true) : '-'),
      },
    ],
  },
  {
    title: 'Preços Gerais',
    children: [
      {
        title: 'Quantidade Total',
        dataIndex: 'overallTotalQuantity',
        width: '80px',
        align: 'center',
        render: (quantity: number) => <S.Text>{quantity || '-'}</S.Text>,
      },
      {
        title: 'Menor',
        dataIndex: 'overallLowestPrice',
        width: '80px',
        align: 'right',
        render: (price: number) => (price ? currencyFormatter(price, true) : '-'),
      },
      {
        title: 'Média',
        dataIndex: 'overallAveragePrice',
        width: '80px',
        align: 'right',
        render: (price: number) => (price ? currencyFormatter(price, true) : '-'),
      },
      {
        title: 'Maior',
        dataIndex: 'overallHighestPrice',
        width: '80px',
        align: 'right',
        render: (price: number) => (price ? currencyFormatter(price, true) : '-'),
      },
    ],
  },
];

export default function SalesStatementIntermediationSummaryTable() {
  const {
    isError,
    isLoading,
    dataSource,
    tableParams,
    fetchTable,
    setDataStore,
    formFilterInstance,
  } = useSaleStatementByTypeStore(
    useShallow(state => ({
      isError: state.intermediationSummaryTable.isError,
      isLoading: state.intermediationSummaryTable.isLoading,
      dataSource: state.intermediationSummaryTable.data,
      tableParams: state.intermediationSummaryTable.tableParams,
      fetchTable: state.handleIntermediationSummaryTable,
      setDataStore: state.setData,
      formFilterInstance: state.intermediationFormFilterInstance,
    })),
  );

  const handleTableChange = useCallback(
    async (pagination: TablePaginationConfig, sorter?: SorterResult<ISummarySaleStatement>) => {
      try {
        await formFilterInstance?.validateFields?.();

        const sortField = !sorter?.order
          ? defaultColumnOrderBy
          : toString(sorter?.field) || defaultColumnOrderBy;

        const sort = sorter?.order === 'descend' ? SorterEnum.DESC : SorterEnum.ASC;

        setDataStore(state => {
          state.intermediationSummaryTable.tableParams.pagination.current = pagination.current;
          state.intermediationSummaryTable.tableParams.pagination.pageSize = pagination.pageSize;
          state.intermediationSummaryTable.tableParams.orderBy = sortField;
          state.intermediationSummaryTable.tableParams.sort = sort;
          state.intermediationSummaryTable.data = [];
        });

        fetchTable();
      } catch (error) {
        message.error('Erro ao buscar os dados');
      }
    },
    [formFilterInstance, setDataStore, fetchTable],
  );

  if (isError) {
    return (
      <ErrorMessage message="Ocorreu um erro inesperado! Não foi possível trazer os dados da tabela. Atualize a página e tente novamente." />
    );
  }

  return (
    <TableContainer data-testid="sales-statement-table">
      <Table
        columns={tableColumns}
        sortDirections={['ascend', 'descend']}
        rowKey={rowKey}
        loading={isLoading}
        pagination={tableParams.pagination}
        dataSource={dataSource}
        onChange={(page: TablePaginationConfig, _, sorter) => {
          handleTableChange(page, sorter as SorterResult<ISummarySaleStatement>);
        }}
        scroll={{ y: 470 }}
      />
    </TableContainer>
  );
}
